import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useConfig } from '../hooks/useConfig';
import { createAuth0Client, Auth0Client } from '@auth0/auth0-spa-js';

interface LoginProps {
  onLogin: (config: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const config = useConfig();
  const [auth0, setAuth0] = useState<Auth0Client | null>(null);

  React.useEffect(() => {
    let mounted = true;
    async function init() {
      try {
        if (config.authProvider !== 'auth0') return;
        if (!config.auth0Domain || !config.auth0ClientId) {
          setError('Thiếu cấu hình Auth0 (domain/clientId)');
          return;
        }
        const client = await createAuth0Client({
          domain: config.auth0Domain,
          clientId: config.auth0ClientId,
          authorizationParams: {
            prompt: 'login',
          },
          cacheLocation: 'localstorage',
        });
        if (mounted) setAuth0(client);
      } catch (e) {
        console.error(e);
        setError('Không thể khởi tạo Auth0');
      }
    }
    init();
    return () => { mounted = false; };
  }, [config.authProvider, config.auth0Domain, config.auth0ClientId]);

  const handleAuth0Login = async () => {
    if (!auth0) return;
    setIsLoading(true);
    setError('');
    try {
      await auth0.loginWithPopup();
      const user = await auth0.getUser();
      const displayName = user?.name || user?.email || 'Người dùng';
      const sheetName = (user as any)?.sub || 'default-sheet';
      const pictureUrl = (user as any)?.picture || '';

      const loginData = {
        displayName,
        sheetName,
        loginTime: new Date().toISOString(),
      };
      localStorage.setItem('loginData', JSON.stringify(loginData));
      if (pictureUrl) {
        localStorage.setItem('userPictureUrl', pictureUrl);
      }
      // Update PWA icons immediately on this tab
      try {
        const w = window as any;
        if (w && typeof w.__updatePWAIcons === 'function') {
          w.__updatePWAIcons();
        }
      } catch {}
      onLogin({ displayName, sheetName });
    } catch (e) {
      console.error('Auth0 login error:', e);
      setError('Đăng nhập thất bại');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}>
      {/* Animated background elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '200px',
        height: '200px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%',
        animation: 'float 6s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '20%',
        right: '15%',
        width: '150px',
        height: '150px',
        background: 'rgba(255,255,255,0.08)',
        borderRadius: '50%',
        animation: 'float 8s ease-in-out infinite reverse',
      }} />

      <Card sx={{ 
        maxWidth: 400, 
        width: '100%', 
        mx: 2,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: 4,
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.2)',
        transform: 'translateY(0)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 35px 60px rgba(0,0,0,0.3)',
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          {/* App Icon */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 3 
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
            }}>
              <Lock sx={{ fontSize: 40, color: 'white' }} />
            </Box>
          </Box>

          <Typography 
            variant="h4" 
            component="h1" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            {config.appName}
          </Typography>

          <Typography 
            variant="body1" 
            align="center" 
            color="text.secondary" 
            sx={{ mb: 4, fontWeight: 500 }}
          >
            {config.displayName}
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.2)'
              }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleAuth0Login}
              disabled={!auth0 || isLoading}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                fontWeight: 600,
                fontSize: '1.05rem',
                textTransform: 'none',
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Đang đăng nhập...
                </>
              ) : (
                'Đăng nhập với Auth0'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
};

export default Login; 